import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import './CategoryProductRow.css';

function stripHtml(text) {
  return String(text || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function buildSpecs(product) {
  const rows = [];
  if (product.brandName) rows.push(['Brand', product.brandName]);
  if (product.sku) rows.push(['Model / SKU', product.sku]);
  if (product.categoryName) rows.push(['Category', product.categoryName]);
  return rows;
}

function CategoryProductRow({ product, categoryTitle }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  const detailUrl = product.url || `/shop/product/${product.slug || product.id}`;
  const maxQty = product.inStock ? Math.min(Math.max(product.stockQuantity, 1), 99) : 1;
  const qtyOptions = Array.from({ length: maxQty }, (_, i) => i + 1);
  const showPrice = product.priceRaw > 0;
  const quoteUrl = `/contact?product=${encodeURIComponent(product.name)}&sku=${encodeURIComponent(product.sku)}`;
  const specs = buildSpecs(product);
  const description = stripHtml(product.longDescription || product.description);
  const pathLabel = categoryTitle || product.categoryName || 'Products';
  const shortName = product.name?.length > 48
    ? `${product.name.slice(0, 48).trim()}…`
    : product.name;

  const handleAddToCart = () => {
    if (!product.inStock || !showPrice) return;
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <article className="category-product-row">
      <div className="category-product-row__image">
        <Link to={detailUrl}>
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            onError={(e) => {
              e.target.src = `https://via.placeholder.com/160x160?text=${encodeURIComponent(product.sku)}`;
            }}
          />
        </Link>
      </div>

      <div className="category-product-row__main">
        <h2 className="category-product-row__title">
          <Link to={detailUrl}>
            {product.name} ({product.sku})
          </Link>
        </h2>
        <p className="category-product-row__path">
          {pathLabel} &gt; {shortName}
        </p>

        {description && (
          <p className="category-product-row__desc">{description}</p>
        )}

        {specs.length > 0 && (
          <table className="category-product-row__specs">
            <tbody>
              {specs.map(([label, value]) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <button
          type="button"
          className="category-product-row__compare"
          onClick={() => navigate(detailUrl)}
        >
          + Add to Compare
        </button>

        <p className={`category-product-row__stock ${product.inStock ? 'in-stock' : 'out-of-stock'}`}>
          {product.inStock ? 'In Stock' : 'Out of Stock'}
        </p>
      </div>

      <div className="category-product-row__buy">
        <div className="category-product-row__price-block">
          {product.hasDiscount && product.originalPrice && (
            <span className="category-product-row__price-was">{product.originalPrice}</span>
          )}
          <span className="category-product-row__price">
            {showPrice ? product.price : 'See Price'}
          </span>
        </div>

        <div className="category-product-row__buy-row">
          <select
            className="category-product-row__qty"
            value={qty}
            disabled={!product.inStock}
            onChange={(e) => setQty(Number(e.target.value))}
            aria-label="Quantity"
          >
            {qtyOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          {showPrice && product.inStock ? (
            <button type="button" className="category-product-row__cart" onClick={handleAddToCart}>
              {added ? 'Added ✓' : 'Add to Cart'}
            </button>
          ) : (
            <Link to={detailUrl} className="category-product-row__cart category-product-row__cart--link">
              See Price
            </Link>
          )}
        </div>

        <Link to={quoteUrl} className="category-product-row__quote">
          + Add to Quote
        </Link>

        {!showPrice && (
          <button type="button" className="category-product-row__price-hint">
            Why don&apos;t we show the price?
          </button>
        )}
      </div>
    </article>
  );
}

export default CategoryProductRow;
